import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import QuillEditor from '../../MasterAdmin/ui/QuillEditor';
import type { MarketingEmailTemplate } from '../../../models/MasterAdmin/marketingEmail';
import {
  DEFAULT_MARKETING_EMAIL_TEMPLATE_HTML,
  MARKETING_EMAIL_FONT_FAMILY,
  MARKETING_EMAIL_FONT_SIZE,
} from './marketingEmailTypography';
import {
  meInputClass,
  meLabelClass,
  meModalOverlayClass,
  meModalPanelClass,
  mePrimaryBtnClass,
  meSecondaryBtnClass,
  meTextareaClass,
} from './marketingEmailUi';

interface TemplateFormModalProps {
  open: boolean;
  template?: MarketingEmailTemplate | null;
  initialSubject?: string;
  initialHtmlBody?: string;
  onClose: () => void;
  onSave: (template: MarketingEmailTemplate) => void;
}

function newTemplateId() {
  return `tpl-${Date.now()}`;
}

const TemplateFormModal: React.FC<TemplateFormModalProps> = ({
  open,
  template,
  initialSubject,
  initialHtmlBody,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');

  useEffect(() => {
    if (open) {
      setName(template?.name ?? '');
      setDescription(template?.description ?? '');
      setSubject(template?.subject ?? initialSubject ?? '');
      setHtmlBody(template?.htmlBody ?? initialHtmlBody ?? DEFAULT_MARKETING_EMAIL_TEMPLATE_HTML);
    }
  }, [open, template, initialSubject, initialHtmlBody]);

  if (!open) return null;

  const handleSave = () => {
    if (!name.trim() || !subject.trim()) return;
    const now = new Date().toISOString();
    onSave({
      id: template?.id ?? newTemplateId(),
      name: name.trim(),
      description: description.trim(),
      subject: subject.trim(),
      htmlBody,
      createdAt: template?.createdAt ?? now,
      updatedAt: now,
    });
    onClose();
  };

  return (
    <div className={meModalOverlayClass}>
      <div className={`${meModalPanelClass} max-w-2xl`}>
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {template ? 'Edit template' : 'Create template'}
          </h2>
          <button type="button" onClick={onClose} className={meSecondaryBtnClass}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={meLabelClass}>Template name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} className={meInputClass} placeholder="Welcome email" />
            </div>
            <div>
              <label className={meLabelClass}>Subject line</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} className={meInputClass} placeholder="Email subject" />
            </div>
          </div>
          <div>
            <label className={meLabelClass}>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className={meTextareaClass}
              placeholder="Short note for your team"
            />
          </div>
          <div>
            <label className={meLabelClass}>Body</label>
            <QuillEditor
              key={template?.id ?? 'new-template'}
              value={htmlBody}
              onChange={setHtmlBody}
              height="240px"
              defaultFontFamily={MARKETING_EMAIL_FONT_FAMILY}
              defaultFontSize={MARKETING_EMAIL_FONT_SIZE}
              useSemanticHtml
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-800">
          <button type="button" onClick={onClose} className={meSecondaryBtnClass}>
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={!name.trim() || !subject.trim()} className={mePrimaryBtnClass}>
            {template ? 'Update template' : 'Create template'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateFormModal;
